import React from "react";

/**
 * Custom hook that sets up keyboard navigation for the Player carousel.
 *
 * Keys handled:
 *   ArrowRight  -> next slide
 *   ArrowLeft   -> previous slide
 *   Escape      -> pause & show modal (or close if modal already visible)
 *   Space       -> toggle play/pause
 *
 * @param {object}   carouselRef  - React ref to <Carousel>
 * @param {boolean}  showModal    - Whether the player modal is visible
 * @param {Function} handleClose  - Callback to exit the player
 * @param {boolean}  isStop       - Whether playback is stopped
 * @param {Function} setIsStop    - Setter for isStop
 * @param {boolean}  play         - Whether auto-advance is active
 * @param {Function} setPlay      - Setter for play
 * @param {Function} setShowModal - Setter for showModal
 */
export const useKeyboardNavigation = (
  carouselRef,
  showModal,
  handleClose,
  isStop,
  setIsStop,
  play,
  setPlay,
  setShowModal
) => {
  React.useEffect(() => {
    const handleKeyDown = async (e) => {
      if (e.code === "ArrowRight") {
        e.preventDefault();
        carouselRef?.current?.onClickNext();
      } else if (e.code === "ArrowLeft") {
        e.preventDefault();
        carouselRef?.current?.onClickPrev();
      } else if (e.code === "Escape") {
        e.preventDefault();
        if (showModal) {
          // If modal is showing, close and go to dashboard
          handleClose();
        } else {
          // If modal is not showing, pause and show modal
          setPlay(false);
          setIsStop(true);
          setShowModal(true);
        }
      } else if (e.code === "Space") {
        e.preventDefault();
        setIsStop(!isStop);
        setPlay(!isStop);
        if (isStop) {
          carouselRef?.current?.autoPlay();
          setShowModal(false);
        } else {
          carouselRef?.current?.clearAutoPlay();
          setShowModal(true);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    carouselRef,
    showModal,
    handleClose,
    isStop,
    setIsStop,
    play,
    setPlay,
    setShowModal,
  ]);
};
